import * as THREE from "three";
import { JOINT_SPHERE_SIZES, JOINT_MATERIALS } from "./jointStyles";

/**
 * Creates visualization for a ball joint: three orthogonal rings (gimbal) showing the 3-DOF rotation.
 */
export function createBallJointVisualization(jointInfo, _parentRot, showJoints, jointMarkerSizes) {
  const { name: jointName, uuid: jointUuid } = jointInfo;

  // max_rotation stored as range[1] per the MJCF convention; fall back to 90°
  const maxRotation =
    Array.isArray(jointInfo.range) &&
    jointInfo.range.length === 2 &&
    Number.isFinite(jointInfo.range[1])
      ? jointInfo.range[1]
      : typeof jointInfo.max_rotation === "number"
        ? jointInfo.max_rotation
        : Math.PI / 2;

  const jointVizGroup = new THREE.Group();
  jointVizGroup.userData.isJointVizGroup = true;
  jointVizGroup.userData.jointName = jointName;
  jointVizGroup.userData.jointUuid = jointUuid;
  jointVizGroup.userData.jointType = "ball";

  const arcRadius = jointMarkerSizes ? jointMarkerSizes.center * 3 : JOINT_SPHERE_SIZES.center * 3;

  // Helper: build a full ring in the plane perpendicular to the given axis
  const createRing = (normalAxis) => {
    const points = [];
    const segments = 48;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const c = Math.cos(angle) * arcRadius;
      const s = Math.sin(angle) * arcRadius;
      if (normalAxis === "x") points.push(new THREE.Vector3(0, c, s));
      else if (normalAxis === "y") points.push(new THREE.Vector3(c, 0, s));
      else points.push(new THREE.Vector3(c, s, 0));
    }
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    const mat = new THREE.LineBasicMaterial(JOINT_MATERIALS.arc);
    const ring = new THREE.Line(geo, mat);
    ring.renderOrder = 1;
    ring.userData.jointUuid = jointUuid;
    ring.userData.jointName = jointName;
    return ring;
  };

  const ringX = createRing("x"); // ring in YZ plane
  const ringY = createRing("y"); // ring in XZ plane
  const ringZ = createRing("z"); // ring in XY plane

  jointVizGroup.add(ringX);
  jointVizGroup.add(ringY);
  jointVizGroup.add(ringZ);

  jointVizGroup.userData.ringX = ringX;
  jointVizGroup.userData.ringY = ringY;
  jointVizGroup.userData.ringZ = ringZ;
  jointVizGroup.userData.maxRotation = maxRotation;

  // updateAll is called when range/axis changes — for ball joints, only max_rotation can change.
  // We scale the rings to give a visual indication of how wide the cone is.
  jointVizGroup.userData.updateAll = (min, max) => {
    const newMax = Number.isFinite(max) ? max : maxRotation;
    const scale = Math.max(0.1, Math.sin(Math.max(0, newMax)));
    // Scale only XY/XZ rings to represent the cone limit;
    // all three rings stay at arcRadius (they communicate DOF, not just limit)
    jointVizGroup.userData.maxRotation = newMax;
    // The full-ring radius doesn't change — the cone limit is communicated through
    // opacity/size of individual rings rather than geometry rescaling for now.
    void scale; // future: adjust scale of rings
  };

  jointVizGroup.visible = !!showJoints;
  ringX.visible = !!showJoints;
  ringY.visible = !!showJoints;
  ringZ.visible = !!showJoints;

  return jointVizGroup;
}

function ensurePivotForBall(bodyGroup, initialPos, initialRot, jointPosLocal) {
  const parent = bodyGroup.parent;
  if (!parent) return null;

  if (!bodyGroup.userData._pivotGroup) {
    const pivot = new THREE.Group();
    pivot.userData.isJointPivot = true;
    parent.add(pivot);
    pivot.add(bodyGroup);
    bodyGroup.userData._pivotGroup = pivot;
  }

  const pivot = bodyGroup.userData._pivotGroup;
  const jointPos = new THREE.Vector3().fromArray(jointPosLocal || [0, 0, 0]);
  const cached = bodyGroup.userData._pivotLocal || [NaN, NaN, NaN];

  if (cached[0] !== jointPos.x || cached[1] !== jointPos.y || cached[2] !== jointPos.z) {
    const bodyRot = initialRot || bodyGroup.quaternion;
    const jointOffsetParent = jointPos.clone().applyQuaternion(bodyRot);
    pivot.position.copy(jointOffsetParent.clone().add(initialPos));
    bodyGroup.position.copy(jointOffsetParent.clone().negate());
    bodyGroup.userData._pivotLocal = [jointPos.x, jointPos.y, jointPos.z];
  }

  return pivot;
}

/**
 * Applies a ball joint value to a body group.
 * Value can be a quaternion [qw, qx, qy, qz]; scalar 0 (or absent) means neutral pose.
 */
export function applyBallJointValue(bodyGroup, joint, value, initialRot, initialPos) {
  const isQuat = Array.isArray(value) && value.length === 4;
  const isNeutral =
    !isQuat ||
    (Math.abs(value[0] - 1) < 1e-6 &&
      Math.abs(value[1]) < 1e-6 &&
      Math.abs(value[2]) < 1e-6 &&
      Math.abs(value[3]) < 1e-6);

  if (isNeutral) {
    if (bodyGroup.userData._pivotGroup) {
      const pivot = bodyGroup.userData._pivotGroup;
      const parent = pivot.parent;
      if (parent) {
        parent.add(bodyGroup);
        bodyGroup.position.copy(initialPos);
        bodyGroup.quaternion.copy(initialRot);
        parent.remove(pivot);
      }
      delete bodyGroup.userData._pivotGroup;
      delete bodyGroup.userData._pivotLocal;
    } else {
      bodyGroup.position.copy(initialPos);
      bodyGroup.quaternion.copy(initialRot);
    }
    return;
  }

  const pivot = ensurePivotForBall(bodyGroup, initialPos, initialRot, joint.position || [0, 0, 0]);
  bodyGroup.quaternion.copy(initialRot);

  if (pivot) {
    const [qw, qx, qy, qz] = value;
    pivot.quaternion.set(qx, qy, qz, qw);
  }
}

/**
 * Updates visibility of ball joint visualization elements.
 */
export function updateBallJointVisibility(jointVizGroup, showJoints) {
  if (!jointVizGroup) return;
  jointVizGroup.visible = showJoints;
  if (jointVizGroup.userData.ringX) jointVizGroup.userData.ringX.visible = showJoints;
  if (jointVizGroup.userData.ringY) jointVizGroup.userData.ringY.visible = showJoints;
  if (jointVizGroup.userData.ringZ) jointVizGroup.userData.ringZ.visible = showJoints;
}
